import { useCallback, useEffect, useRef, useState } from 'react';
import { addTestCases } from '../../../../modules/jira';
import { addTestSteps } from '../../../../modules/teststep';
import { linkIssueToBulkEntities } from '../../../../modules/associations';
import { generateNotificationUrl } from '../../../../lib/notificationUtils';
import { AUTO_CLOSE_MS, SAVE_PHASE } from '../../test-cycles/static-data';
import { createWeblink } from '../../../../modules/traceability';
import { apiRoot } from "../../../../modules/callApi";

const toUuid = (val) => (val && val !== '' ? val : null);

const normalizeStep = ({ step, testData, expectedResult, uri }) => ({
  id: null,
  step: step || '',
  testData: testData || '',
  expectedResult: expectedResult || '',
  uri: uri || '',
});

const buildEntityId = (id, key, projectId) => ({
  entityId: id,
  entityKey: key,
  entityUrl: generateNotificationUrl('TestCase', projectId, key, 'traceability', null),
});

export const useBulkSave = ({
  jiraReviewTCs,
  setJiraReviewTCs,
  selectedStory,
  statuses,
  priorities,
  currentUserId,
  projectUsers,
  projectId,
  refetchData,
  handleClose,
  selectedExisting = new Set(),
  existingMatches = [],
  uploadedFileRef = null,
  confluenceLinkRef = null,
}) => {
  const [savePhase, setSavePhase] = useState(SAVE_PHASE.IDLE);
  const [saveProgress, setSaveProgress] = useState(null);
  const [saveResult, setSaveResult] = useState(null);

  const autoCloseRef = useRef(null);
  const refetchDataRef = useRef(refetchData);
  const handleCloseRef = useRef(handleClose);

  useEffect(() => { refetchDataRef.current = refetchData; }, [refetchData]);
  useEffect(() => { handleCloseRef.current = handleClose; }, [handleClose]);

  // FIX: resetSaveState is defined before the useEffect that calls it so it is
  // in scope. We use a ref to avoid it appearing in the effect's dep array
  // (which would cause an infinite loop since resetSaveState itself sets state).
  const resetSaveState = useCallback(() => {
    clearTimeout(autoCloseRef.current);
    setSavePhase(SAVE_PHASE.IDLE);
    setSaveProgress(null);
    setSaveResult(null);
  }, []);

  // Keep a stable ref so the auto-close timeout can call it without capturing
  // a stale closure.
  const resetSaveStateRef = useRef(resetSaveState);
  useEffect(() => { resetSaveStateRef.current = resetSaveState; }, [resetSaveState]);

  useEffect(() => {
    if (savePhase !== SAVE_PHASE.SUCCESS) return;
    refetchDataRef.current?.();
    autoCloseRef.current = setTimeout(() => {
      resetSaveStateRef.current();
      handleCloseRef.current?.();
    }, AUTO_CLOSE_MS);
    return () => clearTimeout(autoCloseRef.current);
    // FIX: savePhase is the only reactive dep here; reset/close are accessed
    // via stable refs to avoid stale-closure issues without adding them to deps.
  }, [savePhase]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkSave = useCallback(async () => {
    const selected = jiraReviewTCs.filter((tc) => tc.selected);
    const hasNew = selected.length > 0;
    const hasExisting = selectedExisting.size > 0;
    if (!hasNew && !hasExisting) return;
    const uploadedFile = uploadedFileRef?.current;
    const confluenceLink = confluenceLinkRef?.current;

    const storySnapshot = selectedStory;
    const defaultStatus = statuses?.find((s) => s.is_default)?.id ?? null;
    const defaultPriority = priorities?.find((p) => p.is_default)?.id ?? null;
    const defaultOwner = currentUserId ?? projectUsers?.[0]?.id ?? null;

    // If only linking existing (no new TCs to create), go straight to LINKING
    if (!hasNew) {
      setSavePhase(SAVE_PHASE.LINKING);
      setSaveProgress(null);
    } else {
      setSavePhase(SAVE_PHASE.SAVING);
      setSaveProgress({ current: 0, total: selected.length, summary: '' });
    }

    const failed = [];
    const savedTestCases = [];

    for (let i = 0; i < selected.length; i++) {
      const tc = selected[i];
      setSaveProgress({ current: i + 1, total: selected.length, summary: tc.summary });
      try {
        const testcase = {
          summary: tc.summary,
          objective: tc.objective || '',
          preCondition: tc.preCondition || '',
          module: toUuid(tc.module),
          labels: tc.labels || [],
          technologyLayer: toUuid(tc.technologyLayer),
          testsuites: tc.testsuites || [],
          country: tc.country || [],
          priority: toUuid(tc.priority) ?? defaultPriority,
          status: toUuid(tc.status) ?? defaultStatus,
          owner: toUuid(tc.owner) ?? defaultOwner,
          reviewer: null,
          automatedBy: null,
          automationMethod: null,
          automationStatus: null,
          isAutomated: 'No',
          isAutomationNeeded: false,
          reasonForNotAutomated: '',
          estimatedTime: '',
          automationCountry: [],
          notAutomatedCategory: null,
          notAutomatedSubCategory: null,
        };

        const response = await addTestCases({ testcase }, projectId);
        const tcId = response?.test_case?.id;
        const tcKey = response?.test_case?.key;

        if (tc.testSteps?.length > 0) {
          try {
            await addTestSteps({ testSteps: tc.testSteps.map(normalizeStep) }, tcId);
          } catch (err) {
            console.error('Failed to save test steps for TC:', tc.summary, err);
          }
        }

        savedTestCases.push(buildEntityId(tcId, tcKey, projectId));
      } catch (error) {
        failed.push({ _id: tc._id, reason: error?.message || 'Unknown error' });
      }
    }

    const successCount = savedTestCases.length;

    // All new TCs failed — abort before linking
    if (hasNew && successCount === 0) {
      setSavePhase(SAVE_PHASE.ERROR);
      setSaveProgress(null);
      setSaveResult({ successCount: 0, failedCount: failed.length, linkError: false, storyKey: storySnapshot?.key });
      setJiraReviewTCs((prev) =>
        prev.map((tc) => {
          const f = failed.find((x) => x._id === tc._id);
          return f ? { ...tc, saveError: f.reason } : tc;
        }),
      );
      return;
    }

    // Proceed to linking phase
    setSavePhase(SAVE_PHASE.LINKING);
    setSaveProgress(null);

    let linkError = false;

    if (storySnapshot) {
      const existingEntityIds = [...selectedExisting].reduce((acc, id) => {
        const match = existingMatches.find((m) => m.id === id);
        if (match) acc.push(buildEntityId(match.id, match.key, projectId));
        return acc;
      }, []);

      const allEntityIds = [...savedTestCases, ...existingEntityIds];

      if (allEntityIds.length > 0) {
        try {
          await linkIssueToBulkEntities(
            {
              issue: {
                id: storySnapshot.id,
                key: storySnapshot.key,
                priority: storySnapshot.priority || '',
                projectId: storySnapshot.projectId,
              },
              entityIds: allEntityIds,
              entityType: 'TestCase',
              gainssProjectId: projectId,
            },
            projectId,
          );
        } catch (err) {
          console.error('Failed to link test cases to story:', storySnapshot?.key, err);
          linkError = true;
        }
      }
    }

    setSaveResult({ successCount, failedCount: failed.length, linkError, storyKey: storySnapshot?.key });

    if (failed.length > 0) {
      setSavePhase(SAVE_PHASE.PARTIAL);
      refetchDataRef.current?.();
      setJiraReviewTCs((prev) =>
        prev.map((tc) => {
          const f = failed.find((x) => x._id === tc._id);
          return f ? { ...tc, saveError: f.reason } : { ...tc, selected: false };
        }),
      );
    } else {
      for (const tc of savedTestCases) {
        if (uploadedFile) {
          try {
            const formData = new FormData();
            formData.append('files', uploadedFile);
            formData.append('entityType', 'TestCase');
            formData.append('entityId', tc.entityId);
            await fetch(`${apiRoot}attachments?projectId=${projectId}`, {
              method: 'POST', body: formData,
              headers: { Authorization: localStorage.getItem('token') },
            });
          } catch (err) { console.error('Failed to upload attachment:', err); }
        }

        if (confluenceLink) {
          try {
            await createWeblink({
              weblink: {
                entityType: 'TestCase',
                entityId: tc.entityId,
                url: confluenceLink,
                description: 'Confluence link from Jira story',
              }
            });
          } catch (err) { console.error('Failed to save weblink:', err); }
        }
      }

      setSavePhase(SAVE_PHASE.SUCCESS);
    }
  }, [
    jiraReviewTCs, setJiraReviewTCs, selectedStory,
    statuses, priorities, currentUserId, projectUsers,
    projectId, selectedExisting, existingMatches,
  ]);

  return { savePhase, setSavePhase, saveProgress, saveResult, setSaveResult, handleBulkSave, resetSaveState };
};